import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useGetProgram } from '@interxfi/api-client/program/get-program';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { RequiresAccess } from '@interxfi/design-system/access-context';
import { Button } from '@interxfi/design-system/components/button';
import { type DataDefinition, DefinitionBox } from '@interxfi/design-system/components/definition-box/definition-box';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  type WrappingLayoutColumnDef,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Tooltip } from '@interxfi/design-system/components/tooltip/tooltip';
import type { IxmUserauthV1ProgramState } from '@interxfi/types';
import { PageTabRail } from '../../../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../components/router/router-tabbed-navigation';
import { getProgramStateListItems } from '../../../../../../../../../features/userauth/program/program-list-items';
import { ProgramPageHeader } from '../../../../../../../../../features/userauth/program/program-page-header';
import { UpdateProgramCodesAndIdsDrawer } from '../../../../../../../../../features/userauth/program/update-program-codes-and-ids-drawer';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../util/error-handler';

export const Route = createFileRoute('/_app/_platform/_context-required/program/_program/$programId/_program/configuration/clearing')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.ProgramConfiguration,
      label: 'Codes & IDs',
      order: 2,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.ProgramConfiguration>,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `program-${params.programId}-clearing`,
        label: 'Configuration',
        to: `/program/${params.programId}/clearing`,
      }),
    }) satisfies AggregateBreadcrumb,
  component: ProgramClearingConfiguration,
});

const programDetailDefinitions: DataDefinition[] = [
  {
    id: 'programCode',
    title: [{ id: 'programCode', label: 'Program Code' }],
    definition: '4-character code used as a prefix for system generated Display IDs.',
  },
  {
    id: 'shortName',
    title: [{ id: 'shortName', label: 'Short Name' }],
    definition: '16-character-maximum name for the Program that may appear on a customer’s bank statement for ACH transfers.',
  },
  {
    id: 'mpid',
    title: [{ id: 'mpid', label: 'MPID' }],
    definition: 'Broker-dealers Market Participant Identifier assigned by FINRA.',
  },
  {
    id: 'taxVendorId',
    title: [{ id: 'taxVendorId', label: 'Tax Vendor ID' }],
    definition: 'Maps accounts to the correct branding configuration for tax forms.',
  },
  {
    id: 'proxyVendorId',
    title: [{ id: 'proxyVendorId', label: 'Proxy Vendor ID' }],
    definition: 'Maps accounts to the correct branding configuration for shareholder communications.',
  },
];

function getListItems(data: IxmUserauthV1ProgramState | undefined) {
  const items = getProgramStateListItems(data);

  return [
    items.programCode,
    items.data.clearingDetails.shortName,
    items.data.clearingDetails.mpid,
    items.data.clearingDetails.taxVendorId,
    items.data.clearingDetails.proxyVendorId,
  ] as const satisfies ReadonlyListItem[];
}

const rightColumn = { width: { type: 'fixed', width: 320 }, overflow: 'hidden' } satisfies Omit<WrappingLayoutColumnDef, 'render'>;

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load program', 'view this program');

function ProgramClearingConfiguration() {
  const { programId } = Route.useParams();
  const request = { programId } as const;
  const { activeTenantIsPlatform } = useActiveRealmAPIContext();
  const { data, error, isPending } = useGetProgram(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.program), [data?.program]);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <ProgramPageHeader programId={programId} withEntityMenu />
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <PageTabRail
          actions={
            activeTenantIsPlatform ? (
              <RequiresAccess write>
                <UpdateProgramCodesAndIdsDrawer programId={programId} trigger={<Button size="small">Edit</Button>} />
              </RequiresAccess>
            ) : (
              <Tooltip
                activator={
                  <Button disabled size="small">
                    Edit
                  </Button>
                }
                maxWidth={200}
              >
                Please contact the Interchange Team to make changes to your program's Codes &amp; IDs.
              </Tooltip>
            )
          }
          group={TabbedNavigationGroup.ProgramConfiguration}
        />
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <AppReadonlyList isPending={showPending} items={listItems} />,
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...rightColumn,
            render: (isWrapping) => (isWrapping ? null : <DefinitionBox definitions={programDetailDefinitions} />),
          }}
          wrap="forward"
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
